import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import api from '../../services/api';

// Landing page for the emailed reset link. The token is checked before the
// form is shown, so an expired link says so straight away instead of failing
// after the customer has typed a new password.
// Defined at module scope on purpose: a component declared inside
// ResetPasswordPage would be a new type on every render, so React would unmount
// and remount the form and the password field would lose focus after each key.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center px-4 py-3 sm:px-6">
        <a href="/">
          <img
            src="/assets/images/whitebglogo.jpeg"
            alt="SendMyParcel"
            className="h-9 w-auto sm:h-10"
          />
        </a>
      </div>
    </div>
    <div className="mx-auto flex max-w-md flex-col justify-center px-4 py-16">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  </div>
);

const ResetPasswordPage = () => {
  const token = new URLSearchParams(window.location.search).get('token');

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [emailHint, setEmailHint] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setTokenError('This reset link is missing its token. Please request a new one.');
        setChecking(false);
        return;
      }
      try {
        const res = await api.get('/auth/reset-password/validate', { params: { token } });
        if (cancelled) return;
        setTokenValid(true);
        setEmailHint(res.data?.email_hint || null);
      } catch (err) {
        if (cancelled) return;
        setTokenError(
          err.response?.data?.detail ||
            'This reset link is invalid or has expired. Please request a new one.'
        );
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Please choose a password of at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'We could not update your password. The link may have expired - please request a new one.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-6 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Checking your reset link...</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle className="h-7 w-7 text-emerald-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#0A1128]">Password updated</h1>
          <p className="text-slate-600">
            Your password has been changed. Please sign in with your new password.
          </p>
          <Button
            onClick={() => {
              // ?login=required is what makes the Header open the sign-in modal;
              // a bare /login just renders the landing page with nothing to use.
              window.location.href = '/login?login=required';
            }}
            className="w-full bg-[#F17221] hover:bg-[#d9661d]"
          >
            Go to sign in
          </Button>
        </div>
      </Shell>
    );
  }

  if (!tokenValid) {
    return (
      <Shell>
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
            <AlertTriangle className="h-7 w-7 text-amber-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#0A1128]">Link no longer valid</h1>
          <p className="text-slate-600">{tokenError}</p>
          <p className="text-sm text-slate-500">
            Reset links expire after 2 hours and can only be used once.
          </p>
          <Button
            onClick={() => {
              window.location.href = '/?forgot=1';
            }}
            className="w-full bg-[#F17221] hover:bg-[#d9661d]"
          >
            Request a new link
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-extrabold text-[#0A1128]">Set a new password</h1>
        {emailHint && (
          <p className="mt-1 text-sm text-slate-500">for {emailHint}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Hidden but present so password managers can associate the entry */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={emailHint || ''}
          readOnly
          hidden
        />

        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              className="pl-10 pr-10"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm new password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your new password"
              className="pl-10"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (error) setError(null);
              }}
              required
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#F17221] hover:bg-[#d9661d]"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating password...
            </>
          ) : (
            'Update password'
          )}
        </Button>
      </form>
    </Shell>
  );
};

export default ResetPasswordPage;
